import { Button, FilterMenu, SearchBox } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { PERIOD_PRESETS, type PeriodPreset } from '../report-period.ts';
import { useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  HANDOVER_STATUSES,
  isHandoverListingNarrowed,
  NO_HANDOVER_FILTERS,
  type HandoverFilters,
  type HandoverStatus,
} from './handover.ts';
import tools from '../ListTools.module.css';

export type HandoverFilterBarProps = {
  shipping: ShippingClient;
  filters: HandoverFilters;
  search: string;
  onFiltersChange: (filters: HandoverFilters) => void;
  onSearchChange: (search: string) => void;
};

const presetOf = (value: string | null): PeriodPreset | null =>
  PERIOD_PRESETS.find((preset) => preset === value) ?? null;

const statusOf = (value: string | null): HandoverStatus | null =>
  HANDOVER_STATUSES.find((status) => status === value) ?? null;

export const HandoverFilterBar = ({
  shipping,
  filters,
  search,
  onFiltersChange,
  onSearchChange,
}: HandoverFilterBarProps) => {
  const { t } = usePageContext();
  const carriers = useCarriers(shipping).data?.items ?? [];
  const clearLabelOf = (name: string) => t('orders.filter_remove', { name });
  return (
    <div className={tools['listTools']}>
      <SearchBox
        label={t('handover.searchLabel')}
        placeholder={t('handover.searchPlaceholder')}
        value={search}
        onChange={(event) => {
          onSearchChange(event.target.value);
        }}
        onSearch={onSearchChange}
      />
      <div className={tools['filters']} role="group" aria-label={t('orders.filters')}>
        <FilterMenu
          label={t('orders.filter_carrier')}
          options={carriers.map((carrier) => ({ value: carrier.code, label: carrier.name }))}
          value={filters.carrier}
          clearLabel={clearLabelOf(t('orders.filter_carrier'))}
          onChange={(carrier) => {
            onFiltersChange({ ...filters, carrier });
          }}
        />
        <FilterMenu
          label={t('handover.status')}
          options={HANDOVER_STATUSES.map((status) => ({
            value: status,
            label: t(`handover.status_${status}`),
          }))}
          value={filters.status}
          clearLabel={clearLabelOf(t('handover.status'))}
          onChange={(status) => {
            onFiltersChange({ ...filters, status: statusOf(status) });
          }}
        />
        <FilterMenu
          label={t('orders.filter_date')}
          options={PERIOD_PRESETS.map((preset) => ({ value: preset, label: t(`period.${preset}`) }))}
          value={filters.period}
          clearLabel={clearLabelOf(t('orders.filter_date'))}
          onChange={(period) => {
            onFiltersChange({ ...filters, period: presetOf(period) });
          }}
        />
        {isHandoverListingNarrowed({ ...filters, search }) ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onSearchChange('');
              onFiltersChange(NO_HANDOVER_FILTERS);
            }}
          >
            {t('common.clearFilters')}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
